import {useEffect, type JSX} from 'react'
import {Helmet} from 'react-helmet'
import {ThemeProvider} from 'styled-components'
import {LightTheme} from '../theme/light-theme'
import {DarkTheme} from '../theme/dark-theme'
import {configSelector} from '../store/state/config/selectors'
import RoleChecker from '../components/RoleChecker'
import useTypedSelector from '../hooks/use-typed-selector'
import {logInStatusSelector} from '../store/state/session-data/selectors'
import {DimensionsProvider} from '../contexts/dimensions/dimensions-provider'
import {AuthenticatedRoutes} from './authenticated-routes'
import {UnauthenticatedRoutes} from './unauthenticated-routes'
import {useAuthenticatedSemaphore} from '../hooks/use-authenticated-semaphore'
import {LoginStatus} from '../store/state/session-data/state'
import {useDispatch} from 'react-redux'
import {loggingOut} from '../store/state/session-data/action-creators'
import {currentUserSelector} from '../store/state/current-user/selectors'
import {setCustomLogoFlag, setGfcFlag, setUserGuiding} from '../store/state/config/action-creators'
import {BrowserUtils, getTagName} from '../utils/Utils'
import {PageType} from '../components/vessel-filters/data-helper'
import {setSearchVesselTagTerm} from '../store/state/vessel-filter/action-creators'
import {vesselTagsSelector} from '../store/state/vessel-tags/selectors'
import {StrictMode} from '../store/state/users/state'
import {useFleetScoreOverTime} from '../pages/dashboard-v2/components/fleet-score-over-time/contexts/use-fleet-score-over-time'
import {useTagsComponent} from '../pages/dashboard-v2/components/tags-component/contexts/use-tags-component'
// @ts-expect-error this thing needs to be revisited
import {injectUserGuiding} from '../utils/inject-user-guiding'
import {type UserGuidingConfig} from '../store/state/config/state'
import {useIncidentsWidget} from '../pages/dashboard-v2/components/incident-response/contexts/use-incidents-widget'
import {FontStyles as DefaultFontStyles} from '../styles/font/font.styled'
import {FontStyles as GfcFontStyles} from '../styles/font/font-gfc.styled'
import {SuspenseBoundary} from '../components/base/suspense-boundary'
import {FullScreenLoading} from './full-screen-loading'

const App = (): JSX.Element => {
    const dispatch = useDispatch()
    const config = useTypedSelector(configSelector)
    const loginStatus = useTypedSelector(logInStatusSelector)
    const {authenticationSemaphoreStatus} = useAuthenticatedSemaphore()
    const {strictMode, assignedTag} = useTypedSelector(currentUserSelector)
    const existingTags = useTypedSelector(vesselTagsSelector)

    const setSearchVesselTagForTimeline = useFleetScoreOverTime().setSearchVesselTagTerm
    const setSearchVesselTagForTagCompare = useTagsComponent().setSearchVesselTagTerm
    const setSearchVesselTagForIncidentWidget = useIncidentsWidget().setSearchVesselTagTerm

    useEffect(() => {
        async function fetchConfigFlag() {
            try {
                const result = await fetch(
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    `${import.meta.env.BASE_URL}config/${import.meta.env.VITE_ALT_BACKEND ?? 'medulla-ui'}.json`,
                )
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const response = await result.json()
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                dispatch(setGfcFlag(response.gfc))
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                dispatch(setCustomLogoFlag(response.customLogo))
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const formattedEnableFlag =
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    response.userGuidingEnabled == undefined ? true : response.userGuidingEnabled
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const formattedUserGuidFlag =
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    response.userGuidingId == undefined ? '021118067ID' : response.userGuidingId
                const receivedUserresponse: UserGuidingConfig = {
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    userGuidingEnabled: formattedEnableFlag,
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    userGuidingId: formattedUserGuidFlag,
                }
                dispatch(setUserGuiding(receivedUserresponse))
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                await injectUserGuiding(receivedUserresponse)
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                window.userGuiding.identify(formattedUserGuidFlag)
            } catch (error) {
                console.error(error)
            }
        }
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchConfigFlag()
    }, [dispatch])

    const favicon: HTMLLinkElement | null = document.querySelector('link#faviconRel')
    if (favicon) {
        if (!BrowserUtils.isBrowserDarkMode()) {
            favicon.href = `${import.meta.env.BASE_URL}faviconLight.ico`
        }
    }

    const title: string = config.tenantDisplayName
        ? `${config.tenantDisplayName} - Medulla`
        : 'Medulla'

    const darkMode = false

    useEffect(() => {
        if (authenticationSemaphoreStatus) {
            return
        } else {
            if (
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                loginStatus == null ||
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                loginStatus == undefined ||
                loginStatus === LoginStatus.LOGGED_IN
            ) {
                dispatch(loggingOut())
            }
        }
    }, [authenticationSemaphoreStatus, dispatch, loginStatus])

    useEffect(
        () => {
            if (loginStatus === LoginStatus.LOGGED_IN) {
                const assignedTagName = getTagName(existingTags, assignedTag)
                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.INCIDENTS,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )
                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.MY_VESSELS,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )
                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.REPORTS,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )
                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.NETWORK_ASSETS,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )
                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.USB_DEVICES,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )
                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.SOFTWARE_INVENTORY,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )

                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.METRICS,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )

                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.VESSEL_MANAGEMENT,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )
                dispatch(
                    setSearchVesselTagTerm(
                        assignedTagName ? [assignedTagName] : [],
                        PageType.INCIDENTS_REPORT,
                        strictMode === StrictMode.DefaultVisibility,
                    ),
                )
                setSearchVesselTagForTimeline(
                    existingTags.find((tag) => tag.identity === assignedTag),
                )
                setSearchVesselTagForTagCompare(assignedTagName ? [assignedTagName] : [])
                setSearchVesselTagForIncidentWidget(
                    assignedTagName ? [assignedTagName] : [],
                    strictMode === StrictMode.DefaultVisibility,
                )
            }
        }, // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [loginStatus, strictMode, assignedTag, existingTags],
    )

    return (
        <DimensionsProvider>
            {/* TODO: Fix this the next time the file is edited. */}
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            <ThemeProvider theme={darkMode ? DarkTheme : LightTheme}>
                {config.gfcFlag ? <GfcFontStyles /> : <DefaultFontStyles />}
                <RoleChecker rolesAllowed={['DEVELOPER', 'THREAT_ANALYST']}>
                    <Helmet>
                        <title>{title}</title>
                    </Helmet>
                </RoleChecker>

                {loginStatus === LoginStatus.LOGGED_IN ? (
                    <SuspenseBoundary loadingFallback={<FullScreenLoading />}>
                        <AuthenticatedRoutes />
                    </SuspenseBoundary>
                ) : (
                    <UnauthenticatedRoutes />
                )}
            </ThemeProvider>
        </DimensionsProvider>
    )
}

export default App
